// Health check service for calling the backend health check endpoint

// Types matching the backend HealthCheckService
interface ServiceStatus {
  status: 'healthy' | 'error' | 'unknown'
  error: string | null
}

export interface HealthCheckResult {
  overall: 'healthy' | 'degraded'
  timestamp: string
  services: {
    anthropic: ServiceStatus
    openai: ServiceStatus
    algolia: ServiceStatus
    firestore: ServiceStatus
    storage: ServiceStatus
  }
  environment: {
    isEmulator: boolean
    projectId: string
  }
}

export class HealthCheckService {
  /**
   * Call the backend health check endpoint
   * @returns Promise<HealthCheckResult> - Health check results from backend
   */
  static async checkSystemHealth(): Promise<HealthCheckResult> {
    try {
      // Determine the correct URL based on environment
      // Use the same logic as Firebase Functions SDK for consistency
      const isEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true'
      const projectId =
        import.meta.env.VITE_FIREBASE_PROJECT_ID || 'reaction-gif-app'

      const baseUrl = isEmulator
        ? `http://localhost:5001/${projectId}/us-west1`
        : `https://us-west1-${projectId}.cloudfunctions.net`

      const response = await fetch(`${baseUrl}/healthCheck`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      // The backend returns 503 when services are degraded, but this is still valid data
      if (!response.ok && response.status !== 503) {
        throw new Error(
          `Health check failed: ${response.status} ${response.statusText}`
        )
      }

      const result: HealthCheckResult = await response.json()
      return result
    } catch (error) {
      console.error('Error calling health check endpoint:', error)

      // Return a fallback result indicating connection error
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'

      return {
        overall: 'degraded',
        timestamp: new Date().toISOString(),
        services: {
          anthropic: {
            status: 'error',
            error: `Connection failed: ${errorMessage}`,
          },
          openai: {
            status: 'error',
            error: `Connection failed: ${errorMessage}`,
          },
          algolia: {
            status: 'error',
            error: `Connection failed: ${errorMessage}`,
          },
          firestore: {
            status: 'error',
            error: `Connection failed: ${errorMessage}`,
          },
          storage: {
            status: 'error',
            error: `Connection failed: ${errorMessage}`,
          },
        },
        environment: {
          isEmulator: import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true',
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'unknown',
        },
      }
    }
  }

  /**
   * Get appropriate color class for service status
   */
  static getStatusColor(status: ServiceStatus['status']): string {
    switch (status) {
      case 'healthy':
        return 'text-green-600 dark:text-green-400'
      case 'error':
        return 'text-red-600 dark:text-red-400'
      case 'unknown':
      default:
        return 'text-gray-600 dark:text-gray-400'
    }
  }

  /**
   * Get appropriate icon for service status
   */
  static getStatusIcon(status: ServiceStatus['status']): string {
    switch (status) {
      case 'healthy':
        return '✓'
      case 'error':
        return '✗'
      case 'unknown':
      default:
        return '?'
    }
  }

  /**
   * Format service names for display
   */
  static formatServiceName(serviceName: string): string {
    switch (serviceName) {
      case 'anthropic':
        return 'Anthropic AI'
      case 'openai':
        return 'OpenAI'
      case 'algolia':
        return 'Search Index'
      case 'firestore':
        return 'Database'
      case 'storage':
        return 'Storage'
      default:
        return serviceName.charAt(0).toUpperCase() + serviceName.slice(1)
    }
  }
}
